import {isEmpty} from 'lodash';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {BillingUpgradeWantedFeaturesEnum} from '../../../../../libs/shared/billing/business/src/billingUpgradeTypes';
import {BillingUpgradeIcon} from '../../../../../libs/shared/billing/web/src/components/billingUpgradeIcon';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {DropdownPlacement} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownBanner} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownBanner';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {TypedRowValue} from '../../../../../libs/shared/ui/src/components/dropdowns/source/dropdownSource';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CustomFieldModel, CustomFieldResourceTypesEnum} from '../../../../models/api/customFieldModel';
import {BillingUpgradeTooltipWrapper} from '../../../../settings/billing/common/upgrade/billingUpgradeTooltipWrapper';
import {DropdownItemAccentIcon} from '../dropdownItemAccentIcon';
import {CustomFieldItem} from './customFieldItem';

/*
 * Constants.
 */

const dropdownSmallRowHeight = 30;
const dropdownBannerItemHeight = 52;
const dropdownItemHeight = 48;
const dropdownCompactItemHeight = 30;
const dropdownHeight = 342;

enum CustomFieldDropdownItemsEnum {
  BANNER = 'banner',
  CATEGORY = 'category',
  CATEGORY_MARGIN = 'category_margin',
  NEW_ACTION = 'new_action',
  FIELD = 'field',
}

/*
 * Props.
 */

interface CustomFieldDropdownCreateOptions {
  isUpsellRequired: boolean;
  feature: BillingUpgradeWantedFeaturesEnum;
}

interface CustomFieldDropdownProps {
  popoverProps: PopoverTransferProps;
  customFields: ReadonlyArray<CustomFieldModel>;
  selectedCustomFieldId?: number;
  allSelectedCustomFieldIds: ReadonlyArray<number>;
  isCreateDisabled?: boolean;
  shouldAllowAlreadySelectedFields?: boolean;
  customFieldResourceType: CustomFieldResourceTypesEnum;
  renderCreateDisabledTooltip?: Renderer;
  renderBannerText?: Renderer;
  renderCustomBanner?: Renderer;
  onSelect: (model: CustomFieldModel) => void;
  onCreateClick?: () => void;
  renderSpecialCustomFieldType?: RendererOf<CustomFieldModel>;
  renderCustomFieldName?: RendererOf<CustomFieldModel>;
  renderCustomFieldIcon?: (customField: CustomFieldModel) => IconName | undefined;
  placement?: DropdownPlacement;
  createUpsellOptions?: CustomFieldDropdownCreateOptions;
  headerRightIcon?: ReactNode;
  isCompact?: boolean;
}

type CustomFieldDropdownRowTypedValue =
  | TypedRowValue<CustomFieldDropdownItemsEnum.BANNER, undefined>
  | TypedRowValue<CustomFieldDropdownItemsEnum.CATEGORY, ReactNode>
  | TypedRowValue<CustomFieldDropdownItemsEnum.CATEGORY_MARGIN, ReactNode>
  | TypedRowValue<CustomFieldDropdownItemsEnum.NEW_ACTION, undefined>
  | TypedRowValue<CustomFieldDropdownItemsEnum.FIELD, CustomFieldModel>;

/*
 * Style.
 */

const StyledDropdownCategory = styled(DropdownCategory)`
  margin-top: 4px;
`;

const StyledDropdownItemAccentIcon = styled(DropdownItemAccentIcon)`
  padding: 4px;
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 190px;
`;

const StyledUpsellDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 190px;
`;

const StyledEmptyRendererDiv = styled.div`
  margin: 35px;
`;

const StyledCreateButtonLinkDiv = styled.div`
  padding-top: 20px;
  display: flex;
  justify-content: center;
`;

const StyledCreateButtonTextDiv = styled.div`
  ${ellipsis()};
  font-weight: ${fontWeights.semibold};
  max-width: 200px;
`;

const StyledAskAdminTextDiv = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
`;

/*
 * Component.
 */

export const CustomFieldDropdown: FC<CustomFieldDropdownProps> = (props) => {
  const {
    customFields,
    popoverProps,
    allSelectedCustomFieldIds,
    selectedCustomFieldId,
    isCreateDisabled,
    shouldAllowAlreadySelectedFields,
    customFieldResourceType,
    renderCreateDisabledTooltip,
    renderBannerText,
    onSelect,
    onCreateClick,
    renderCustomFieldName,
    renderCustomFieldIcon,
    renderSpecialCustomFieldType,
    renderCustomBanner,
    placement = 'top-start',
    createUpsellOptions,
    headerRightIcon,
    isCompact,
  } = props;
  const isShowingBanner = Boolean(renderBannerText || renderCustomBanner);
  const [query, setQuery] = useState('');
  const data = useMemo(
    () => computeDropdownItems(customFields, isShowingBanner, onCreateClick, isCompact),
    [customFields, isShowingBanner, onCreateClick, isCompact],
  );
  const filteredData = useMemo(() => {
    // If we do not have a query, return all the items.
    if (query.trim() === '') {
      return data;
    }
    // Only show custom fields that match.
    return data.filter(
      (item) =>
        item.type === CustomFieldDropdownItemsEnum.FIELD &&
        item.value.name.toLowerCase().includes(query.toLowerCase()),
    );
  }, [data, query]);

  return (
    <SearchDropdown
      {...popoverProps}
      data={filteredData}
      renderRow={(item) => {
        switch (item.type) {
          case CustomFieldDropdownItemsEnum.BANNER:
            return renderCustomBanner ? renderCustomBanner() : renderBanner(renderBannerText);
          case CustomFieldDropdownItemsEnum.CATEGORY:
            return <DropdownCategory>{item.value}</DropdownCategory>;
          case CustomFieldDropdownItemsEnum.CATEGORY_MARGIN:
            return <StyledDropdownCategory>{item.value}</StyledDropdownCategory>;
          case CustomFieldDropdownItemsEnum.NEW_ACTION:
            return renderCreateRow(
              customFieldResourceType,
              onCreateClick,
              isCreateDisabled,
              renderCreateDisabledTooltip,
              createUpsellOptions,
            );
          case CustomFieldDropdownItemsEnum.FIELD:
          default:
            return renderCustomField(
              item.value,
              allSelectedCustomFieldIds,
              selectedCustomFieldId,
              onSelect,
              shouldAllowAlreadySelectedFields,
              renderCustomFieldName,
              renderCustomFieldIcon,
              renderSpecialCustomFieldType,
              isCompact,
            );
        }
      }}
      query={query}
      onQueryChange={setQuery}
      renderOverlay={() => renderEmptyState(query, filteredData, onCreateClick)}
      findRowHeight={(item) => {
        switch (item?.type) {
          case CustomFieldDropdownItemsEnum.BANNER:
            return dropdownBannerItemHeight;
          case CustomFieldDropdownItemsEnum.CATEGORY:
          case CustomFieldDropdownItemsEnum.NEW_ACTION:
            return dropdownSmallRowHeight;
          case CustomFieldDropdownItemsEnum.CATEGORY_MARGIN:
            return dropdownSmallRowHeight + 4;
          case CustomFieldDropdownItemsEnum.FIELD:
          default:
            return isCompact ? dropdownCompactItemHeight : dropdownItemHeight;
        }
      }}
      headerTitle={renderDropdownTitle(customFieldResourceType)}
      height={dropdownHeight}
      placement={placement}
      headerRightIcon={headerRightIcon}
    />
  );
};

/*
 * Helpers.
 */

function computeDropdownItems(
  customFields: ReadonlyArray<CustomFieldModel>,
  isShowingBanner?: boolean,
  onCreateClick?: (arg: string) => void,
  isCompact?: boolean,
): ReadonlyArray<CustomFieldDropdownRowTypedValue> {
  const items: Array<CustomFieldDropdownRowTypedValue> = [];

  if (onCreateClick) {
    items.push(
      {
        type: CustomFieldDropdownItemsEnum.CATEGORY,
        value: <Trans id="custom-field-dropdown-create-category">Create</Trans>,
      },
      {
        type: CustomFieldDropdownItemsEnum.NEW_ACTION,
        value: undefined,
      },
    );
  }

  if (isShowingBanner) {
    items.unshift({
      type: CustomFieldDropdownItemsEnum.BANNER,
      value: undefined,
    });
  }

  // If we do not have any custom fields, we will only show the create option.
  if (!customFields.length) {
    return items;
  }
  const dropdownItems = [...items];
  if (!isCompact) {
    dropdownItems.push({
      type: CustomFieldDropdownItemsEnum.CATEGORY_MARGIN,
      value: <Trans id="custom-field-dropdown-fields-category">Fields</Trans>,
    });
  }
  dropdownItems.push(
    ...customFields.map<TypedRowValue<CustomFieldDropdownItemsEnum.FIELD, CustomFieldModel>>((value) => ({
      type: CustomFieldDropdownItemsEnum.FIELD,
      value,
    })),
  );

  return dropdownItems;
}

function renderDropdownTitle(customFieldResourceType: CustomFieldResourceTypesEnum) {
  switch (customFieldResourceType) {
    case CustomFieldResourceTypesEnum.ACCOUNT:
      return <Trans id="account-custom-field-dropdown-title">Front account fields</Trans>;
    case CustomFieldResourceTypesEnum.CARD:
      return <Trans id="card-custom-field-dropdown-title">Front contact fields</Trans>;
    default:
      return <Trans id="custom-field-dropdown-title">Front custom fields</Trans>;
  }
}

function renderBanner(renderBannerText?: Renderer) {
  return renderBannerText && <DropdownBanner>{renderBannerText()}</DropdownBanner>;
}

function renderCreateRow(
  customFieldResourceType: CustomFieldResourceTypesEnum,
  onCreateClick?: () => void,
  isDisabled: boolean = false,
  renderCreateDisabledTooltip?: Renderer,
  createUpsellOptions?: CustomFieldDropdownCreateOptions,
) {
  if (!onCreateClick) {
    return null;
  }

  if (createUpsellOptions && createUpsellOptions.isUpsellRequired) {
    return (
      <BillingUpgradeTooltipWrapper
        isBillingUpgradeRequired={createUpsellOptions.isUpsellRequired}
        feature={createUpsellOptions.feature}
        placement="left"
      >
        <DropdownItem
          isDisabled={isDisabled}
          onSelect={(event) => {
            event.preventDefault();
            onCreateClick();
          }}
        >
          <StyledDropdownItemAccentIcon label={null} iconName="plusCircleFilledSmall" shouldHideBackground />
          <StyledUpsellDropdownItemContent>
            {renderCreateRowTitle(customFieldResourceType)}
            <BillingUpgradeIcon />
          </StyledUpsellDropdownItemContent>
        </DropdownItem>
      </BillingUpgradeTooltipWrapper>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <DropdownItem
          renderTooltip={isDisabled ? renderCreateDisabledTooltip : undefined}
          tooltipPlacement="top"
          tooltipPersistent
          isDisabled={isDisabled}
          onSelect={(event) => {
            event.preventDefault();
            onCreateClick();
          }}
        >
          <StyledDropdownItemAccentIcon label={null} iconName="plusCircleFilledSmall" shouldHideBackground />
          <StyledDropdownItemContent>
            {renderCreateRowTitle(customFieldResourceType)}
            {isDisabled && <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} />}
          </StyledDropdownItemContent>
        </DropdownItem>
      )}
    </UserThemeConsumer>
  );
}

function renderCreateRowTitle(customFieldResourceType: CustomFieldResourceTypesEnum) {
  switch (customFieldResourceType) {
    case CustomFieldResourceTypesEnum.ACCOUNT:
      return <Trans id="account-custom-field-create-row-title">New account field</Trans>;
    case CustomFieldResourceTypesEnum.CARD:
      return <Trans id="card-custom-field-create-row-title">New contact field</Trans>;
    default:
      return <Trans id="custom-field-create-row-title">New custom field</Trans>;
  }
}

function renderCustomField(
  item: CustomFieldModel,
  allSelectedCustomFieldIds: ReadonlyArray<number>,
  selectedCustomFieldId: number | undefined,
  onSelect: (model: CustomFieldModel) => void,
  shouldAllowAlreadySelectedFields?: boolean,
  renderCustomFieldName?: RendererOf<CustomFieldModel>,
  renderCustomFieldIcon?: (customField: CustomFieldModel) => IconName | undefined,
  renderSpecialCustomFieldType?: RendererOf<CustomFieldModel>,
  shouldHideFieldType?: boolean,
) {
  const isDisabled = shouldAllowAlreadySelectedFields ? false : allSelectedCustomFieldIds.includes(item.id);
  const isChecked = selectedCustomFieldId === item.id;
  return (
    <CustomFieldItem
      item={item}
      isChecked={isChecked}
      isDisabled={isDisabled}
      onSelect={onSelect}
      renderCustomFieldName={renderCustomFieldName}
      renderSpecialCustomFieldType={renderSpecialCustomFieldType}
      renderCustomFieldIcon={renderCustomFieldIcon}
      shouldHideFieldType={shouldHideFieldType}
    />
  );
}

function renderEmptyState(
  query: string,
  items: ReadonlyArray<CustomFieldDropdownRowTypedValue>,
  onCreateClick?: (arg: string) => void,
) {
  if (!isEmpty(items)) {
    return null;
  }
  if (!query) {
    return <DropdownEmpty />;
  }

  if (!onCreateClick) {
    return (
      <StyledEmptyRendererDiv>
        <DropdownEmpty />
        <StyledAskAdminTextDiv>
          <Trans id="custom-field-empty-ask-admin-create">
            Ask an administrator to create the fields you need
          </Trans>
        </StyledAskAdminTextDiv>
      </StyledEmptyRendererDiv>
    );
  }

  return (
    <StyledEmptyRendererDiv>
      <DropdownEmpty />
      <StyledCreateButtonLinkDiv onClick={() => onCreateClick(query)}>
        <InlineLinkButton>
          <StyledCreateButtonTextDiv>
            <Trans id="custom-field-empty-create-link">Create &quot;{query}&quot;</Trans>
          </StyledCreateButtonTextDiv>
        </InlineLinkButton>
      </StyledCreateButtonLinkDiv>
    </StyledEmptyRendererDiv>
  );
}
